import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Field, change, touch } from "redux-form";
import { connect } from "react-redux";
import { Typography } from "@material-ui/core";
import * as _ from "lodash";
import Grid from "@material-ui/core/Grid";
import PropTypes from "prop-types";
import renderTextField from "../../../_shared/utils/renderTextField";
import renderSelectField from "../../../_shared/utils/renderSelectField";
import renderDatePicker from "../../../_shared/utils/renderDatePicker";
import renderCheckbox from "../../../_shared/utils/renderCheckbox";
import { selectProjectStatuses } from "../../projectSelectors";
import { selectUserRoles } from "../../../login/loginSelector";
import { getMaxWeight, plAdminWeight } from "../../../../roleaccess/utils";
import { STATUS_CODE } from "../../../../utilities/statusUtils";
import { REAL_ADMIN_R, REAL_AU_R } from "../../../../roleaccess/adGroups";

const hasDeskAuditDateAccess = (userRole = []) => (
  userRole.includes(REAL_AU_R) || userRole.includes(REAL_ADMIN_R)
);

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(3),
    width: "100%",
  },
  closingStatus: {
    margin: 0,
  },
  charCount: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  charCountError: {
    fontSize: '0.75rem',
    color: theme.palette.error.main,
    marginTop: theme.spacing(0.5),
  },
}));

/* Custom render function for project name with character count */
const renderProjectNameField = (classes) => ({ meta: { visited, touched, error }, input, ...custom }) => {
  const currentLength = input.value ? input.value.length : 0;
  const maxLength = 50;
  const isAtLimit = currentLength >= maxLength;
  
  return (
    <React.Fragment>
      {renderTextField({ meta: { visited, touched, error }, input, ...custom })}
      <div className={isAtLimit ? classes.charCountError : classes.charCount}>
        {currentLength}/{maxLength} characters
      </div>
    </React.Fragment>
  );
};

export const ProjectDetails = props => {
  const classes = useStyles();
  const { closingStatusOptions, form, userRole, dispatch } = props;
  const handleChange = event => {
    let value = event.target.value;

    event.target.value = parseInt(value);
  };

  /* Retain Desk Audit Closing Actual Date for Closed/Locked, clear it for other statuses. */
  const handleStatusChange = (event, newValue) => {
    const newStatusCode = newValue && newValue.value;
    if (newStatusCode !== STATUS_CODE.CLOSED && newStatusCode !== STATUS_CODE.LOCKED) {
      dispatch(change(form, 'desk_audit_closing_actual_date', null));
    } else {
      /* Touch the field so validation error shows immediately when switching to Closed */
      dispatch(touch(form, 'desk_audit_closing_actual_date'));
    }
  };
  
  /* Check if user is PL Admin */
  const isPLAdmin = getMaxWeight(userRole) === plAdminWeight();

  /* Check if user can edit desk audit date */
  const canEditDeskAuditDate = userRole && userRole.includes && hasDeskAuditDateAccess(userRole);

  return (
    <div className={classes.root}>
      <Typography variant='h5' gutterBottom>
        Project Details
      </Typography>
      <Grid
        container
        item
        alignItems='center'
        spacing={2}
        xl={12}
        xs={12}
        lg={12}
        md={12}>
        {/* <Field name="status_code" component={renderTextField} type="hidden" /> */}
        <Grid item xl={12} xs={12} lg={12} md={12}>
          <Field
            name='closing_name'
            component={renderProjectNameField(classes)}
            inputLabel='Project Name'
            inputType='text'
            fullWidth
            InputProps={{ inputProps: { maxLength: 50 } }}
          />
        </Grid>
        <Grid item xl={6} xs={6} lg={6} md={6}>
          <Field
            inputLabel='Projected Closing Date'
            name='closing_start_ts'
            component={renderDatePicker}
            inpuType='date'
            placeholder='MM/DD/YYYY'
            dateFormat='MM/DD/YYYY'
            fullWidth
            /* normalize={value => moment(value).valueOf()} */
            /* format={value => moment(value).format('MM/DD/YYYY')} */
          />
        </Grid>
        <Grid item xl={6} xs={6} lg={6} md={6}>
          <Field
            inputLabel='Desk Audit Closing Actual Date'
            name='desk_audit_closing_actual_date'
            component={renderDatePicker}
            inpuType='date'
            placeholder='MM/DD/YYYY'
            dateFormat='MM/DD/YYYY'
            fullWidth
            disabled={!canEditDeskAuditDate}
          />
        </Grid>
        <Grid item xl={6} xs={6} lg={6} md={6}>
          <Field
            disabled={form === "projectCreate"}
            inputLabel='Closing Status'
            type='text'
            defaultValue={closingStatusOptions[0]}
            showInputAdornment={false}
            component={renderSelectField}
            options={closingStatusOptions}
            name='status_code_desc'
            fullWidth
            onChange={handleStatusChange}
          />
        </Grid>
        <Grid item xl={4} xs={4} lg={4} md={4}>
          <Field
            inputLabel='Project/Store Sequence #'
            type='number'
            inputDefaultValue={0}
            showInputAdornment={false}
            component={renderTextField}
            name='project_seq_nbr'
            fullWidth
            onChange={handleChange}
            normalize={value => _.toNumber(value)}
            InputProps={{ inputProps: { min: 0 } }}
          />
        </Grid>
        <Grid item xl={4} xs={4} lg={4} md={4}>
          <Field
            inputLabel='Sellers'
            type='number'
            inputDefaultValue={0}
            showInputAdornment={false}
            component={renderTextField}
            name='seller_count'
            onChange={handleChange}
            fullWidth
            normalize={value => _.toNumber(value)}
            InputProps={{ inputProps: { min: 0 } }}
          />
        </Grid>
        <Grid item xl={4} xs={4} lg={4} md={4}>
          <Field
            inputLabel='Parcels'
            type='number'
            inputDefaultValue={0}
            showInputAdornment={false}
            component={renderTextField}
            onChange={handleChange}
            name='parcel_count'
            normalize={value => _.toNumber(value)}
            fullWidth
            InputProps={{ inputProps: { min: 0 } }}
          />
        </Grid>
        <Grid item xl={4} xs={4} lg={4} md={4}>
          <Field
            inputLabel='Project Entity Rec ID'
            type='text'
            showInputAdornment={false}
            component={renderTextField}
            name='project_entity_rec_id'
            fullWidth
            disabled={!canEditDeskAuditDate}
          />
        </Grid>
        <Grid item xl={12} xs={12} lg={12} md={12}>
          <Field
            name='discretionary_approval'
            component={renderCheckbox}
            label='Discretionary Approval'
            disabled={isPLAdmin}
          />
        </Grid>
      </Grid>
    </div>
  );
};
const mapStateToProps = (state) => {
  const closingStatusOptions = selectProjectStatuses(state);
  const userRole = selectUserRoles(state);
  return {
    closingStatusOptions,
    userRole,
  };
};

ProjectDetails.defaultProps = {
  userRole: [],
};

ProjectDetails.propTypes = {
  closingStatusOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ).isRequired,
  form: PropTypes.string.isRequired,
  userRole: PropTypes.arrayOf(PropTypes.string),
};

const ConnectedProjectDetails = connect(mapStateToProps)(ProjectDetails);

export default ConnectedProjectDetails;
